import {
  For,
  Show,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
  useContext,
  type Component,
  type JSX,
  type ParentComponent,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { formatWallClockTime } from '~/shared/lib/wallClock';
import { useNavigate } from '@solidjs/router';
import { createQuery } from '@tanstack/solid-query';
import { ArrowRight, CalendarClock, Search, Settings as SettingsIcon, User } from 'lucide-solid';
import { makePersisted } from '@solid-primitives/storage';
import { useI18n } from '~/shared/lib/i18n';
import { useShortcut } from '~/shared/lib/shortcuts';
import { haptic } from '~/shared/lib/haptics';
import { NAVIGABLE_ITEMS, navLabelOf } from '~/widgets/shell/nav-items';
import { canOpenSettingsNode, canSeeScreen, menuEntry } from '~/shared/access/sections';
import { holds } from '~/shared/lib/permission-implications';
import { CREATE_BOOKING_HREF, CREATE_BOOKING_PERMISSION } from '~/features/schedule/create-booking-link';
import { useAuth } from '~/shared/api/auth-context';
import { SETTINGS_NODES } from '~/shared/api/settings-api';
import { SETTINGS_MODULE_LABEL } from '~/widgets/settings/settingsModuleLabel';
import { clientsApi } from '~/shared/api/clients-api';
import { bookingApi } from '~/shared/api/booking-api';
import { formatPhoneRu } from '~/shared/lib/formatters';
import { formatMemberId } from '~/shared/lib/member-id';
import { cn } from '~/shared/lib/cn';
import { useModalTrap } from '~/shared/ui/focus-trap';

export interface Command {
  id: string;
  title: string;
  subtitle?: string;
  section: 'navigation' | 'actions' | 'settings' | 'clients' | 'bookings' | 'recent';
  keywords?: string[];
  icon?: JSX.Element;
  perform: () => void | Promise<void>;
  when?: () => boolean;
}

interface CommandPaletteContextValue {
  open: () => boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
  registerCommand: (cmd: Command) => () => void;
  registerCommands: (cmds: Command[]) => () => void;
  setNavigate: (fn: (to: string) => void) => void;
  setCanOpen: (fn: (section: string) => boolean) => void;
}

const PaletteContext = createContext<CommandPaletteContextValue>();

export const CommandPaletteProvider: ParentComponent = (props) => {
  const [open, setOpen] = createSignal(false);
  const [commands, setCommands] = createSignal<Command[]>([]);
  const [navigate, setNavigateRef] = createSignal<(to: string) => void>(() => undefined);
  const [canOpen, setCanOpenRef] = createSignal<(section: string) => boolean>(() => false);
  const [recentIds, setRecentIds] = makePersisted(createSignal<string[]>([]), {
    name: 'sportpark.palette.recent',
    storage: localStorage,
  });

  const registerCommand = (cmd: Command) => {
    setCommands((list) => {
      if (list.some((c) => c.id === cmd.id)) return list;
      return [...list, cmd];
    });
    return () => {
      setCommands((list) => list.filter((c) => c.id !== cmd.id));
    };
  };

  const registerCommands = (cmds: Command[]) => {
    const removers = cmds.map(registerCommand);
    return () => removers.forEach((r) => r());
  };

  const api: CommandPaletteContextValue = {
    open,
    show: () => setOpen(true),
    hide: () => setOpen(false),
    toggle: () => setOpen((v) => !v),
    registerCommand,
    registerCommands,
    setNavigate: (fn) => setNavigateRef(() => fn),
    setCanOpen: (fn) => setCanOpenRef(() => fn),
  };

  const pushRecent = (id: string) => {
    setRecentIds((prev) => [id, ...prev.filter((p) => p !== id)].slice(0, 8));
  };

  return (
    <PaletteContext.Provider value={api}>
      {props.children}
      <PaletteRoot
        open={open()}
        onClose={() => setOpen(false)}
        commands={commands()}
        recentIds={recentIds()}
        onRun={pushRecent}
        navigate={navigate()}
        canOpen={canOpen()}
      />
    </PaletteContext.Provider>
  );
};

export const useCommandPalette = (): CommandPaletteContextValue => {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used within <CommandPaletteProvider>');
  return ctx;
};

const score = (cmd: Command, query: string): number => {
  if (!query) return 0;
  const q = query.toLowerCase();
  const haystack = [cmd.title, cmd.subtitle ?? '', ...(cmd.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  if (haystack.includes(q)) {
    return cmd.title.toLowerCase().includes(q) ? 100 : 50;
  }
  let j = 0;
  for (let i = 0; i < haystack.length && j < q.length; i++) {
    if (haystack[i] === q[j]) j++;
  }
  return j === q.length ? 10 : 0;
};

const SECTION_ORDER: Record<Command['section'], number> = {
  recent: 0,
  actions: 1,
  navigation: 2,
  settings: 3,
  clients: 4,
  bookings: 5,
};

const PaletteRoot: Component<{
  open: boolean;
  onClose: () => void;
  commands: Command[];
  recentIds: string[];
  onRun: (id: string) => void;
  navigate: (to: string) => void;
  canOpen: (section: string) => boolean;
}> = (props) => {
  const { t } = useI18n();
  const [query, setQuery] = createSignal('');
  const [activeIdx, setActiveIdx] = createSignal(0);
  let inputRef: HTMLInputElement | undefined;

  const visible = createMemo<Command[]>(() =>
    props.commands.filter((c) => !c.when || c.when()),
  );

  const [debouncedQuery, setDebouncedQuery] = createSignal('');
  let debounceTimer: number | undefined;
  createEffect(() => {
    const q = query().trim();
    if (debounceTimer) window.clearTimeout(debounceTimer);
    if (!props.open || q.length < 2) {
      setDebouncedQuery('');
      return;
    }
    debounceTimer = window.setTimeout(() => setDebouncedQuery(q), 180);
  });
  onCleanup(() => {
    if (debounceTimer) window.clearTimeout(debounceTimer);
  });

  const clientsQuery = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: ['palette-clients', debouncedQuery()],
    queryFn: ({ signal }) => clientsApi.search(debouncedQuery(), 6, { signal }),
    enabled: debouncedQuery().length >= 2 && props.canOpen('/clients'),
    staleTime: 10_000,
  }));

  const bookingsQuery = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: ['palette-bookings', debouncedQuery()],
    queryFn: ({ signal }) =>
      bookingApi.list({
        q: debouncedQuery(),
        pageSize: 5,
        sort: 'startAt',
        order: 'desc',
        includeCancelled: false,
      }, { signal }),
    enabled: debouncedQuery().length >= 2 && props.canOpen('/bookings'),
    staleTime: 10_000,
  }));

  const dynamicCommands = createMemo<Command[]>(() => {
    if (!debouncedQuery()) return [];
    const out: Command[] = [];
    if (clientsQuery.data && props.canOpen('/clients')) {
      for (const c of clientsQuery.data.items) {
        const fullName = [c.lastName, c.firstName, c.patronymic].filter(Boolean).join(' ');
        out.push({
          id: `client:${c.id}`,
          title: fullName,
          subtitle: `${formatMemberId(c.memberPrefix, c.memberNumber)} · ${formatPhoneRu(c.phone)}`,
          section: 'clients',
          keywords: [c.memberNumber, c.phone, c.firstName, c.lastName],
          icon: <User class="h-4 w-4" />,
          perform: () => {
            props.navigate(`/clients?open=${c.id}`);
          },
        });
      }
    }
    if (bookingsQuery.data && props.canOpen('/bookings')) {
      for (const b of bookingsQuery.data.items) {
        const time = formatWallClockTime(b.startAt);
        const dateLabel = `${b.startAt.slice(8, 10)}.${b.startAt.slice(5, 7)}.${b.startAt.slice(0, 4)}`;
        out.push({
          id: `booking:${b.id}`,
          title: `${b.clientName} · ${b.courtName}`,
          subtitle: `${dateLabel} · ${time} · ${b.id.slice(-6).toUpperCase()}`,
          section: 'bookings',
          keywords: [b.id.slice(-6), b.clientName, b.courtName],
          icon: <CalendarClock class="h-4 w-4" />,
          perform: () => {
            props.navigate(`/bookings?open=${b.id}`);
          },
        });
      }
    }
    return out;
  });

  const filtered = createMemo<Command[]>(() => {
    const q = query().trim();
    const items = visible();
    const dynamic = dynamicCommands();
    if (!q) {
      const recent = props.recentIds
        .map((id) => items.find((c) => c.id === id))
        .filter((x): x is Command => Boolean(x))
        .map<Command>((c) => ({ ...c, section: 'recent' }));
      return [
        ...recent,
        ...items.filter((c) => c.section !== 'settings' && !recent.some((r) => r.id === c.id)),
      ];
    }
    const staticMatches = items
      .map((c) => ({ cmd: c, s: score(c, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.cmd);
    return [...staticMatches, ...dynamic];
  });

  const grouped = createMemo(() => {
    const groups = new Map<Command['section'], Command[]>();
    for (const cmd of filtered()) {
      const bucket = groups.get(cmd.section) ?? [];
      bucket.push(cmd);
      groups.set(cmd.section, bucket);
    }
    return [...groups.entries()].sort(([a], [b]) => SECTION_ORDER[a] - SECTION_ORDER[b]);
  });

  const flat = createMemo(() => grouped().flatMap(([, items]) => items));

  createEffect(on(query, () => setActiveIdx(0)));

  createEffect(() => {
    if (props.open) {
      setQuery('');
      setActiveIdx(0);
      queueMicrotask(() => inputRef?.focus());
    }
  });

  const runCommand = (cmd: Command) => {
    props.onRun(cmd.id);
    props.onClose();
    queueMicrotask(() => {
      haptic('light');
      void cmd.perform();
    });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!props.open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      props.onClose();
      return;
    }
    const items = flat();
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((idx) => (idx + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((idx) => (idx - 1 + items.length) % items.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = items[activeIdx()];
      if (target) runCommand(target);
    }
  };

  onMount(() => {
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => document.removeEventListener('keydown', onKeyDown));
  });

  return (
    <Show when={props.open}>
      <Portal>
        <div
          ref={useModalTrap({ initialFocus: () => inputRef })}
          class="fixed inset-0 z-[1100] flex items-start justify-center pt-[12vh]"
          role="dialog"
          aria-modal="true"
          aria-label="Командная палитра"
        >
          <button
            type="button"
            aria-label="Закрыть"
            class="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={props.onClose}
          />
          <div class="glass-strong relative w-full max-w-[640px] mx-4 flex flex-col max-h-[70vh] overflow-hidden animate-palette-in">
            <div class="flex items-center gap-3 px-4 h-14 border-b border-hairline">
              <Search class="h-5 w-5 text-tertiary" />
              <input
                ref={inputRef}
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                placeholder={t('palette.placeholder')}
                class="flex-1 bg-transparent text-[15px] text-primary placeholder:text-tertiary outline-none"
                autocomplete="off"
                spellcheck={false}
              />
              <kbd class="tabular text-[11px] text-tertiary px-1.5 py-0.5 rounded bg-white/8 border border-hairline">
                esc
              </kbd>
            </div>

            <div class="flex-1 overflow-y-auto py-2" role="listbox">
              <Show
                when={flat().length > 0}
                fallback={
                  <div class="px-4 py-10 text-center text-[14px] text-tertiary">
                    {t('palette.empty')}
                  </div>
                }
              >
                <For each={grouped()}>
                  {([section, items]) => (
                    <div class="py-1">
                      <p class="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wider text-tertiary">
                        {t(`palette.section${capitalize(section)}` as never) as string}
                      </p>
                      <For each={items}>
                        {(cmd) => {
                          const index = () => flat().indexOf(cmd);
                          return (
                            <button
                              type="button"
                              role="option"
                              aria-selected={activeIdx() === index()}
                              class={cn(
                                'press w-full text-left flex items-center gap-3 px-4 h-11 text-[14px]',
                                activeIdx() === index()
                                  ? 'bg-white/10 text-primary'
                                  : 'hover:bg-white/5 text-secondary',
                              )}
                              onMouseEnter={() => setActiveIdx(index())}
                              onClick={() => runCommand(cmd)}
                            >
                              <span class="w-5 h-5 flex items-center justify-center text-tertiary">
                                {cmd.icon ?? <ArrowRight class="h-4 w-4" />}
                              </span>
                              <span class="flex-1 truncate">{cmd.title}</span>
                              <Show when={cmd.subtitle}>
                                <span class="text-[12px] text-tertiary">
                                  {cmd.subtitle}
                                </span>
                              </Show>
                            </button>
                          );
                        }}
                      </For>
                    </div>
                  )}
                </For>
              </Show>
            </div>

            <div class="border-t border-hairline px-4 h-9 flex items-center gap-4 text-[11px] text-tertiary">
              <span>{t('palette.navigateHint')}</span>
              <span>{t('palette.selectHint')}</span>
              <span class="ml-auto">{t('palette.closeHint')}</span>
            </div>
          </div>

          <style>{`
            @keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }
            @keyframes palette-in {
              from { transform: translateY(-12px) scale(0.98); opacity: 0 }
              to   { transform: translateY(0) scale(1);       opacity: 1 }
            }
            .animate-fade-in   { animation: fade-in      var(--duration-base) var(--ease-ios) both; }
            .animate-palette-in{ animation: palette-in   var(--duration-base) var(--ease-ios) both; }
          `}</style>
        </div>
      </Portal>
    </Show>
  );
};

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const useDefaultPaletteCommands = (): void => {
  const palette = useCommandPalette();
  const navigate = useNavigate();
  const { session } = useAuth();
  const permissions = (): readonly string[] => session()?.user.permissions ?? [];
  const canOpen = (section: string): boolean => canSeeScreen(section, permissions());
  palette.setCanOpen(canOpen);
  const canCreateBooking = (): boolean =>
    canOpen('/schedule') && holds(session()?.user.permissions ?? [], CREATE_BOOKING_PERMISSION);

  palette.setNavigate(navigate);

  onMount(() => {
    const navCommands: Command[] = NAVIGABLE_ITEMS.map((item) => ({
      id: `nav:${item.href}`,
      title: navLabelOf(item, session()?.user.roles ?? []),
      subtitle: item.href,
      section: 'navigation',
      keywords: item.keywords,
      perform: () => navigate(menuEntry(item.href, permissions()) ?? item.href),
      when: () => menuEntry(item.href, permissions()) !== null,
    }));

    const actionCommands: Command[] = [
      {
        id: 'action:new-booking',
        title: 'Создать бронь',
        section: 'actions',
        keywords: ['booking', 'бронь', 'создать'],
        perform: () => navigate(CREATE_BOOKING_HREF),
        when: canCreateBooking,
      },
    ];

    const settingsCommands: Command[] = SETTINGS_NODES.filter((n) => !n.comingSoon).map((n) => ({
      id: `settings:${n.id}`,
      title: n.title,
      subtitle: `Настройки · ${SETTINGS_MODULE_LABEL[n.module]}`,
      section: 'settings',
      keywords: [n.subtitle, ...n.keywords],
      icon: <SettingsIcon class="h-4 w-4" />,
      perform: () => navigate(n.href),
      when: () => canOpenSettingsNode(n, permissions()),
    }));

    const cleanup = palette.registerCommands([...navCommands, ...actionCommands, ...settingsCommands]);
    onCleanup(cleanup);
  });

  useShortcut(['cmd+k', 'ctrl+k'], () => palette.toggle(), {
    preventDefault: true,
    allowInInputs: true,
  });
};
